'use client';

import { useTranslations } from 'use-intl';
import { ArrowRightEndOnRectangleIcon } from '@heroicons/react/24/outline';
import useAccount from '@/lib/hooks/useAccount';
import useAuthDrawer from '../auth-drawer-provider/hooks/useAuthDrawer';
import { SIGN_IN_DRAWER_ID } from '../sign-in-drawer';

interface SignInTriggerProps {
  className?: string;
}

/**
 * Guest-only "Sign In" button that opens the AuthDrawerProvider's sign-in
 * drawer. Renders nothing for authenticated users — sibling chrome (the
 * AccountButton popover) covers their navigation needs.
 *
 * Intentionally co-located with the drawer rather than living inside the
 * header tastic so that any surface that wants to expose a sign-in entry
 * point (mobile menu, role-gated CTA, etc.) can drop the same component in.
 */
const SignInTrigger = ({ className = '' }: SignInTriggerProps) => {
  const translate = useTranslations();
  const { loggedIn, isLoading } = useAccount();
  const { mode, open } = useAuthDrawer();

  if (isLoading || loggedIn) return null;

  const isDrawerOpen = mode === 'sign-in';

  return (
    <button
      type="button"
      onClick={() => open('sign-in')}
      aria-haspopup="dialog"
      aria-controls={SIGN_IN_DRAWER_ID}
      aria-expanded={isDrawerOpen}
      className={`text-14 hover:text-primary inline-flex items-center gap-1 font-semibold text-gray-700 underline-offset-4 hover:underline ${className}`.trim()}
    >
      <ArrowRightEndOnRectangleIcon aria-hidden className="h-4 w-4" />
      <span>{translate('auth.header-sign-in')}</span>
    </button>
  );
};

export default SignInTrigger;
